import { Injectable } from '@angular/core';
import { BrowserProvider, getAddress, isAddress } from 'ethers';
import { ActionSheetController } from '@ionic/angular';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';

// WalletConnect Project ID — get one free at https://cloud.walletconnect.com
export const WC_PROJECT_ID = '22fb8e94981e557e9d1cc2bafed8ab77';

type WalletAuthHeaders = { 'X-Wallet-Address': string; 'X-Wallet-Signature': string; 'X-Wallet-Timestamp': string };
type WalletAuthCacheEntry = { headers: WalletAuthHeaders; expiresAt: number };

export function normalizeDeepLink(link: string): string {
  if (!link) return link;
  try {
    let normalized = link.trim();
    let decoded = normalized;
    let attempts = 0;

    while (attempts < 3) {
      if (/^(wc%3A|wc%3a|https%3A|https%3a)/.test(decoded)) {
        const next = decodeURIComponent(decoded);
        if (next === decoded) break;
        decoded = next;
      } else {
        break;
      }
      attempts += 1;
    }

    return decoded;
  } catch {
    // Keep original link if decoding fails.
  }
  return link;
}

export function toMetaMaskWalletConnectUniversalLink(wcUri: string): string {
  return `https://metamask.app.link/wc?uri=${encodeURIComponent(wcUri)}`;
}

// Repli "userInfo" (ancien flux "code d'activation", supprimé) utilisé sur plusieurs pages
// (home/earn/earn-portfolio/dao/dao-proposal/p2p) quand Preferences.UserConnection est absent.
// Certains comptes de longue date ont encore ce blob en cache sous une forme qu'on ne contrôle
// plus — un JSON.parse non protégé plantait silencieusement toute la fonction appelante (donc
// balance/solde jamais chargé) si le blob était malformé ou d'une forme inattendue.
export function resolveLegacyUserInfoAddress(raw: string | null | undefined): string {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw)?.Address;
    return typeof parsed === 'string' && isAddress(parsed) ? parsed : '';
  } catch (e) {
    console.error('[resolveLegacyUserInfoAddress] parse', e);
    return '';
  }
}

export const ETH_CHAIN = {
  chainId: 1,
  chainIdHex: '0x1',
  name: 'Ethereum Mainnet',
  currency: 'ETH',
  rpcUrl: 'https://ethereum-rpc.publicnode.com',
  explorerUrl: 'https://etherscan.io',
};

// Sepolia — UNIQUEMENT pour tester le claim IrisRewardsClaim en local avant tout déploiement
// mainnet (voir trade-rewards.page.ts, gated par !environment.production). Ne jamais utiliser en
// prod : le vrai IRISTOKEN n'existe que sur ETH_CHAIN.
export const SEPOLIA_TEST_CHAIN = {
  chainId: 11155111,
  chainIdHex: '0xaa36a7',
  name: 'Sepolia',
  currency: 'ETH',
  rpcUrl: 'https://ethereum-sepolia-rpc.publicnode.com',
  explorerUrl: 'https://sepolia.etherscan.io',
};

export const BSC_CHAIN = {
  chainId: 56,
  chainIdHex: '0x38',
  name: 'BNB Smart Chain',
  currency: 'BNB',
  rpcUrl: 'https://bsc-dataseed.binance.org/',
  explorerUrl: 'https://bscscan.com',
};

export const BASE_CHAIN = {
  chainId: 8453,
  chainIdHex: '0x2105',
  name: 'Base',
  currency: 'ETH',
  rpcUrl: 'https://base-rpc.publicnode.com',
  explorerUrl: 'https://basescan.org',
};

export const ARBITRUM_CHAIN = {
  chainId: 42161,
  chainIdHex: '0xa4b1',
  name: 'Arbitrum One',
  currency: 'ETH',
  rpcUrl: 'https://arbitrum-one-rpc.publicnode.com',
  explorerUrl: 'https://arbiscan.io',
};

// Lancée le 01/07/2026 — L2 Arbitrum Orbit de Robinhood pour les actions tokenisées.
export const ROBINHOOD_CHAIN = {
  chainId: 4663,
  chainIdHex: '0x1237',
  name: 'Robinhood Chain',
  currency: 'ETH',
  rpcUrl: 'https://rpc.mainnet.chain.robinhood.com',
  explorerUrl: 'https://robinhoodchain.blockscout.com',
};

// Arc (Circle) — chainId 5042 (0x13b2) vérifié en direct via 3 RPC publics indépendants (voir
// IrisApi/ZeroDevAgentExecutor.ChainIds), ajoutée le 2026-09-17. `currency: 'USDC'` — sa devise
// NATIVE est l'USDC lui-même (double représentation 18 décimales natif / 6 décimales interface
// ERC-20 à 0x3600...0000, même solde sous-jacent), jamais un actif volatil comme les 5 autres
// chaînes EVM.
export const ARC_CHAIN = {
  chainId: 5042,
  chainIdHex: '0x13b2',
  name: 'Arc',
  currency: 'USDC',
  rpcUrl: 'https://arc-rpc.publicnode.com',
  explorerUrl: 'https://explorer.arc.io',
};

const WC_METHODS = [
  'eth_sendTransaction',
  'eth_signTransaction',
  'eth_sign',
  'personal_sign',
  'eth_signTypedData',
  'eth_signTypedData_v4',
  'wallet_switchEthereumChain',
  'wallet_addEthereumChain',
];

const WC_EVENTS = ['accountsChanged', 'chainChanged', 'disconnect'];
const WC_DEEPLINK_CHOICE_KEY = 'WALLETCONNECT_DEEPLINK_CHOICE';
const WC_MOBILE_SIGN_OPEN_DELAY_MS = 800;
const WC_METAMASK_SIGN_OPEN_DELAY_MS = 4_500;
// Borne l'attente initiale de connexion (modale "Connecting to {wallet}…") — sans ça, un relay WC
// suspendu (WebView Android mise en arrière-plan pendant que le wallet est au premier plan, deep
// link qui n'aboutit pas) laisse `wcProvider.connect()` ne jamais se résoudre ni rejeter, ce qui
// bloque le spinner de connexion indéfiniment. Généreux (l'utilisateur doit ouvrir le wallet et
// approuver manuellement) pour ne jamais interrompre une connexion réelle en cours.
const WC_CONNECT_TIMEOUT_MS = 120_000;
// Même problème que WC_CONNECT_TIMEOUT_MS mais pour une demande de signature déjà en session
// (approve/claim/withdraw/placeSellOrder/…) — patchWcProvider() relance bien l'ouverture du wallet
// après un délai si la réponse n'arrive pas (voir SIGN_METHODS ci-dessous), mais sans ce timeout
// l'`await` de la requête elle-même reste bloqué indéfiniment si le relay ne délivre jamais la
// réponse (WebView backgroundée pendant la signature, relay froid), quel que soit le nombre de
// tentatives de réouverture du wallet — le bouton "Étape X/3 — …" restait alors bloqué pour
// toujours, sans erreur, exactement comme WC_CONNECT_TIMEOUT_MS avant son ajout.
const WC_SIGN_TIMEOUT_MS = 120_000;
// Même problème que WC_CONNECT_TIMEOUT_MS mais côté wallet INJECTÉ (MetaMask/Trust extension) —
// eth_requestAccounts n'a par défaut AUCUN timeout : si l'extension ne répond jamais (cas connu
// de MetaMask : une demande de connexion précédente reste "en attente" dans sa queue interne et
// bloque silencieusement toute nouvelle requête tant qu'elle n'est ni approuvée ni rejetée),
// l'`await` reste bloqué pour toujours, comme WC_CONNECT_TIMEOUT_MS avant son ajout. 45s : marge
// au-dessus des "30-40 secondes" déjà annoncées à l'utilisateur (ConnectingWalletHint) sans faire
// abandonner une extension juste lente/verrouillée à déverrouiller.
const INJECTED_CONNECT_TIMEOUT_MS = 45_000;
// 2026-09-12 — signalé par l'utilisateur : sur Telegram mobile (app Telegram sur téléphone, PAS
// Telegram Desktop où ça fonctionne), le wallet se connecte mais la demande de signature "Iris
// Auth Request" tourne indéfiniment, les données de l'agent ne chargent jamais. Cause : contrairement
// à connectInjected() ci-dessus, signFreshAuth() n'avait AUCUN timeout sur signer.signMessage() —
// sur mobile, cet appel part via WalletConnect qui doit faire un aller-retour OS (bascule vers
// l'app wallet puis retour au WebView de Telegram) ; ce retour peut casser silencieusement dans le
// WebView embarqué de Telegram mobile (contrairement à Telegram Desktop, où WalletConnect affiche
// un QR code scanné par un mobile séparé — aucun aller-retour du même WebView en jeu). Sans
// timeout, la Promise ne se résout ni ne rejette JAMAIS → spinner infini. Même valeur que
// INJECTED_CONNECT_TIMEOUT_MS, par cohérence.
const SIGN_MESSAGE_TIMEOUT_MS = 45_000;
const APP_URL = 'https://app.iristoken.io';
const ETH_RPC_FALLBACK_URLS = [
  ETH_CHAIN.rpcUrl,
  'https://eth.llamarpc.com',
  'https://1rpc.io/eth',
];
const BSC_RPC_FALLBACK_URLS = [
  BSC_CHAIN.rpcUrl,
  'https://bsc-rpc.publicnode.com',
  'https://1rpc.io/bnb',
];
const BASE_RPC_FALLBACK_URLS = [BASE_CHAIN.rpcUrl, 'https://1rpc.io/base'];
const ARBITRUM_RPC_FALLBACK_URLS = [ARBITRUM_CHAIN.rpcUrl, 'https://1rpc.io/arb'];
// Chaîne lancée le 01/07/2026 — pas encore d'alternative RPC tierce établie.
const ROBINHOOD_RPC_FALLBACK_URLS = [ROBINHOOD_CHAIN.rpcUrl];
const WC_RPC_FALLBACK_METHODS = new Set([
  'eth_blockNumber',
  'eth_call',
  'eth_chainId',
  'eth_estimateGas',
  'eth_feeHistory',
  'eth_gasPrice',
  'eth_getBalance',
  'eth_getCode',
  'eth_getLogs',
  'eth_getTransactionByHash',
  'eth_getTransactionCount',
  'eth_getTransactionReceipt',
  'eth_maxPriorityFeePerGas',
  'net_version',
]);

export type WalletType = 'injected' | 'metamask-mobile' | 'walletconnect';

export interface WalletOption {
  id: WalletType;
  name: string;
  iconName?: string;       // ion-icon name
  iconColor?: string;
  badge?: string;
  badgeColor?: string;
}

@Injectable({ providedIn: 'root' })
export class Web3Service {
  private wcProvider: any = null;
  // Timestamp de la dernière requête de signature réussie (ms). 0 = jamais utilisé / session restaurée.
  private wcLastSignAt = 0;
  // Supprime l'ouverture du wallet jusqu'à ce timestamp (ms). Utilisé après succès du polling RPC.
  private _suppressWalletOpenUntil = 0;
  private walletConnectAndroidPatchApplied = false;
  private injectedAccountsListenerApplied = false;

  constructor(private actionSheetCtrl: ActionSheetController) {}

  // ── Environment detection ──────────────────────────────────────────────────

  isTelegramMiniApp(): boolean {
    return !!(window as any).Telegram?.WebApp?.initData;
  }

  // 2026-09-12, décision de l'utilisateur — TOUS les bugs de signature/session rencontrés sur les
  // Agents IA cette semaine (spinner infini, boucle de reconnexion, "0 action exécutée"...) sont
  // arrivés sur la mini-app Telegram et l'app Android native, jamais sur le web ou Telegram
  // Desktop (aller-retour OS vers l'app wallet fragile dans ces deux WebViews embarquées, voir
  // SIGN_MESSAGE_TIMEOUT_MS). Plutôt que rustiner chaque cas un par un, l'accès en ÉCRITURE aux
  // Agents est fermé sur ces deux plateformes le temps de stabiliser (voir agent-subscribe.page.ts)
  // — utilisé pour un blocage dur, pas juste un bandeau, donc jamais désactivé en dev comme
  // requiresExplicitReconnect() ci-dessous (on veut le voir se déclencher tel quel en test).
  isAgentsRestrictedPlatform(): boolean {
    const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    return this.isTelegramMiniApp() || isAndroidNative;
  }

  // La reconnexion explicite (bandeau "Session expirée — reconnectez votre wallet") n'est utile
  // que là où une session WalletConnect "vivante" en mémoire peut en réalité pointer vers un relay
  // mort/froid : la mini-app Telegram (WebView embarquée) et l'app Android native (Capacitor).
  // Ailleurs — desktop, navigateur mobile normal, ou navigateur intégré d'un wallet (MetaMask/
  // Trust Wallet in-app browser, qui expose window.ethereum directement, sans WalletConnect) — la
  // session est soit inexistante soit fiable, et cette étape en plus n'est qu'une friction inutile.
  // `Capacitor.getPlatform() === 'android'` (pas juste isNativePlatform()) : iOS natif n'a pas ce
  // problème connu, pas de raison de l'y imposer aussi sans le constater.
  requiresExplicitReconnect(): boolean {
    if (!environment.production) return false;
    const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    return this.isTelegramMiniApp() || isAndroidNative;
  }

  // « Faut-il afficher Reconnecter à l'entrée d'une page d'action on-chain ? » — appelé par
  // toutes les pages (buy-iris, token-swap, earn, p2p-*, learn-*, …) dans ionViewWillEnter.
  //
  // Sur Android natif ET Mini App Telegram : renvoie TOUJOURS true (demande explicite de
  // l'utilisateur, 2026-09-09 : « la mini-app fonctionne déjà ainsi, faisons pareil »). Sur ces
  // plateformes, une session WC qui SEMBLE vivante peut pointer vers un relay mort/froid (WebView
  // mis en arrière-plan, app redémarrée entre-temps…) → une signature partirait dans le vide. On
  // force donc l'utilisateur à repasser par « Reconnecter » à chaque entrée de page d'action ;
  // sur Android ce clic déclenche une VRAIE reconnexion complète (voir connectWithChoice), ce qui
  // garantit un relay chaud avant toute signature. `_chainId` non lu ici (comportement plateforme,
  // pas dépendant de la chaîne) mais gardé dans la signature — plusieurs pages le passent.
  //
  // Ailleurs (desktop, navigateur mobile classique, navigateur in-app d'un wallet) : jamais de
  // friction — wallet injecté toujours dispo, ou vraie vérif de session.
  async needsWalletReconnect(expectedAddress?: string, _chainId = ETH_CHAIN.chainId): Promise<boolean> {
    if (!expectedAddress) return false;                 // pas de wallet en cache → bouton « Connecter »
    if (this.hasUsableInjectedProvider()) return false; // wallet injecté → toujours disponible
    const isAndroidNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
    if (this.isTelegramMiniApp() || isAndroidNative) return true;
    return !(await this.hasActiveWriteSession(expectedAddress, _chainId));
  }

  isMobile(): boolean {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  private isAndroid(): boolean {
    return /Android/i.test(navigator.userAgent);
  }

  detectedWalletName(): string | null {
    const eth = (window as any).ethereum;
    if (!eth) return null;
    if (eth.isMetaMask) return 'MetaMask';
    if (eth.isTrust || eth.isTrustWallet) return 'Trust Wallet';
    if (eth.isCoinbaseWallet) return 'Coinbase Wallet';
    if (eth.isRabby) return 'Rabby';
    return 'Wallet détecté';
  }

  // ── Available wallet options given the environment ─────────────────────────

  // Vrai partout où un wallet est injecté directement dans la page (navigateur DApp de MetaMask/
  // Trust Wallet, ou extension desktop) ET qu'on n'est pas dans la Mini App Telegram (qui n'injecte
  // jamais de wallet) — inutile de repasser par WalletConnect pour rouvrir le wallet dans lequel
  // on est déjà. Auparavant réservé à `localhost` (confort de dev) ; généralisé partout, car avant
  // ce fix un navigateur DApp wallet en prod se voyait forcé vers WalletConnect à tort.
  private hasUsableInjectedProvider(): boolean {
    return !this.isTelegramMiniApp() && !!(window as any).ethereum;
  }

  // BUG CORRIGÉ (2026-09-11, signalé par l'utilisateur : reste sur le prix/rental de l'ANCIEN
  // wallet après avoir changé de compte directement dans MetaMask, sans repasser par "Connecter"
  // dans l'app) — aucun listener `accountsChanged` n'existait nulle part sur le provider injecté.
  // L'adresse mise en cache (AccountService/Preferences 'UserConnection') ne se mettait donc à
  // jour QUE via une action EXPLICITE dans l'app (connectWallet/reconnectWallet) ; un changement
  // de compte fait dans l'extension elle-même passait inaperçu indéfiniment — chaque page
  // continuait de lire/écrire pour l'ancien wallet (mauvais prix, mauvais rental affiché) alors
  // que MetaMask avait déjà basculé sur le nouveau. `eth.on('accountsChanged', ...)` est
  // l'événement standard EIP-1193 — tout wallet injecté conforme (MetaMask, Trust, Rabby...) le
  // déclenche automatiquement à chaque changement de compte, sans action supplémentaire requise.
  //
  // callback reçoit la nouvelle adresse (checksummée) ou null si déconnecté (accounts vide) — à
  // l'appelant (app.component.ts) de décider quoi en faire (jamais dans Web3Service : dépendrait
  // d'AccountService, qui dépend déjà de Web3Service — dépendance circulaire).
  watchInjectedAccountsChanged(callback: (address: string | null) => void): void {
    if (this.injectedAccountsListenerApplied || this.isTelegramMiniApp()) return;
    const eth = (window as any).ethereum;
    if (!eth?.on) return;

    this.injectedAccountsListenerApplied = true;
    eth.on('accountsChanged', (accounts: string[]) => {
      if (!accounts?.length) { callback(null); return; }
      try { callback(getAddress(accounts[0])); }
      catch { callback(accounts[0]); }
    });
  }

  getWalletOptions(): WalletOption[] {
    if (this.hasUsableInjectedProvider()) {
      return [{
        id: 'injected',
        name: this.detectedWalletName() ?? 'Wallet détecté',
        iconName: 'wallet-outline',
        iconColor: 'success',
        badge: 'Détecté',
        badgeColor: 'success',
      }];
    }

    return [{
      id: 'walletconnect',
      name: 'WalletConnect',
      iconName: 'scan-outline',
      iconColor: 'tertiary',
      badge: '+300 wallets',
      badgeColor: 'tertiary',
    }];
  }

  // ── Connection methods ─────────────────────────────────────────────────────

  async connect(type: WalletType): Promise<string> {
    switch (type) {
      case 'injected':       return this.connectInjected();
      case 'metamask-mobile':return this.connectMobileDeepLink('metamask');
      case 'walletconnect':  return this.connectWalletConnect();
    }
  }

  // Utilisé par les boutons "Connecter/Reconnecter le wallet" qui n'ont pas leur propre écran de
  // sélection (contrairement à /verification qui liste tous les getWalletOptions()) — un seul
  // choix pertinent par contexte, donc pas besoin d'action sheet.
  async connectWithChoice(): Promise<string> {
    if (this.hasUsableInjectedProvider()) return this.connectInjected();

    // APP ANDROID NATIVE UNIQUEMENT : VRAIE reconnexion à chaque clic sur « Reconnecter » — on
    // purge la session existante (forceWcClear) et on relance le flux WalletConnect COMPLET
    // (modale wallet → approbation dans le wallet → retour à l'app). Demandé explicitement par
    // l'utilisateur (2026-09-09) : une session simplement restaurée peut avoir un relay mort, et
    // « ça affiche juste Approve sans qu'il se passe rien ». Il faut une session FRAÎCHE, relay
    // chaud garanti. Le web et la Mini App Telegram ne sont PAS concernés (déjà OK là-bas).
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      await this.forceWcClear();
      return this.connectWalletConnect();
    }

    // Web / navigateur mobile / Mini App : comportement d'origine — connectWalletConnect() gère
    // le QR et la réutilisation de session.
    return this.connectWalletConnect();
  }

  // Utilisé uniquement par le bouton explicite "Changer de wallet" — purge la session
  // WalletConnect existante et force le sélecteur de compte (wallet_requestPermissions) plutôt que
  // de réutiliser silencieusement la permission déjà accordée comme le fait connectWithChoice().
  async connectWithWalletPicker(): Promise<string> {
    await this.forceWcClear();
    if (this.hasUsableInjectedProvider()) {
      const eth = (window as any).ethereum;
      try {
        await eth.request({ method: 'wallet_requestPermissions', params: [{ eth_accounts: {} }] });
      } catch {
        // Refusé ou non supporté par ce wallet — on retente quand même la connexion normale
        // ci-dessous, qui réutilisera le compte déjà autorisé si besoin.
      }
      return this.connectInjected();
    }
    return this.connectWalletConnect();
  }

  /** Browser-injected provider (MetaMask extension, Rabby, Coinbase, etc.) */
  private async connectInjected(): Promise<string> {
    const eth = (window as any).ethereum;
    if (!eth) throw new Error('no_provider');
    // BUG CORRIGÉ (2026-09-11, signalé par l'utilisateur : "Connexion au wallet..." reste bloqué
    // indéfiniment alors que MetaMask est bien détecté et ouvert) — voir INJECTED_CONNECT_TIMEOUT_MS.
    const accounts: string[] = await Promise.race([
      eth.request({ method: 'eth_requestAccounts' }),
      new Promise<string[]>((_, reject) =>
        setTimeout(() => reject(new Error('injected_connect_timeout')), INJECTED_CONNECT_TIMEOUT_MS)),
    ]);
    if (!accounts.length) throw new Error('no_accounts');
    await this.switchToEth(eth);
    // Checksummé (EIP-55) : certains wallets (notamment via WalletConnect, voir plus bas)
    // renvoient une adresse tout en minuscule — la normaliser ici garantit qu'une seule forme
    // canonique est jamais stockée/comparée en aval (Preferences, requêtes IrisApi, etc.).
    return getAddress(accounts[0]);
  }

  private getWalletConnectMetadata() {
    return {
      name: 'IrisApp',
      description: 'IrisApp — AI-driven ecosystem',
      url: APP_URL,
      icons: [`${APP_URL}/assets/icon/favicon.png`],
    };
  }

  // AppKit (modale WalletConnect) appelle window.open(...) avec le deep link natif du wallet
  // choisi (ex: metamask://wc?uri=..., trust://wc?uri=...) — voir onConnectMobile/openHref dans
  // @reown/appkit-controllers. Sur Android, on réécrit ce lien en universal link https(s) AVANT
  // de l'ouvrir via openDeepLink(), qui sait déjà gérer correctement le contexte Telegram Mini App
  // (Telegram.WebApp.openLink(), jamais location.href — voir son commentaire). Un schéma brut
  // (metamask:// / trust://) passé à location.href ou à openLink() échoue avec
  // net::ERR_UNKNOWN_URL_SCHEME dans le WebView Android de Telegram : openLink() n'accepte QUE du
  // http(s), et location.href n'y déclenche pas la résolution app-link de l'OS comme dans un vrai
  // navigateur — d'où ce fix scindé en deux : toujours produire un lien https, puis toujours
  // l'ouvrir via openDeepLink() plutôt que location.href brut.
  private patchWalletConnectLinksForAndroid(): void {
    if (this.walletConnectAndroidPatchApplied || !this.isAndroid()) return;

    this.walletConnectAndroidPatchApplied = true;
    const originalOpen = window.open.bind(window);
    window.open = (url?: string | URL, target?: string, features?: string): Window | null => {
      const href = url ? String(url) : '';
      const fixedWalletLink = this.rewriteAndroidWcWalletLink(href);
      if (fixedWalletLink) {
        this.openDeepLink(fixedWalletLink);
        return null;
      }
      return originalOpen(url as any, target, features);
    };
  }

  private rewriteAndroidWcWalletLink(link: string): string | null {
    return this.rewriteTrustWalletWcLink(link) ?? this.rewriteMetaMaskWcLink(link);
  }

  private rewriteTrustWalletWcLink(link: string): string | null {
    if (!link || !this.isTrustWalletWcLink(link)) return null;

    try {
      const parsed = new URL(link);
      const rawUri = parsed.searchParams.get('uri');
      if (!rawUri) return null;
      const wcUri = normalizeDeepLink(rawUri);
      if (!wcUri.startsWith('wc:')) return null;
      // Universal link https (jamais le schéma brut trust://) — seul format accepté par
      // Telegram.WebApp.openLink() dans openDeepLink(), et tout aussi valide dans un navigateur
      // Android classique (résolution app-link OS standard).
      return `https://link.trustwallet.com/wc?uri=${encodeURIComponent(wcUri)}`;
    } catch {
      return null;
    }
  }

  private isTrustWalletWcLink(link: string): boolean {
    const lower = link.toLowerCase();
    return lower.includes('link.trustwallet.com/wc') || lower.includes('trust://wc');
  }

  private rewriteMetaMaskWcLink(link: string): string | null {
    if (!link || !this.isMetaMaskWcLink(link)) return null;

    try {
      const parsed = new URL(link);
      const rawUri = parsed.searchParams.get('uri');
      if (!rawUri) return null;
      const wcUri = normalizeDeepLink(rawUri);
      if (!wcUri.startsWith('wc:')) return null;
      return toMetaMaskWalletConnectUniversalLink(wcUri);
    } catch {
      return null;
    }
  }

  private isMetaMaskWcLink(link: string): boolean {
    const lower = link.toLowerCase();
    return lower.includes('metamask.app.link/wc') || lower.includes('metamask://wc');
  }

  /** Deep-link to MetaMask or Trust Wallet mobile app */
  private openDeepLink(link: string) {
    if (!link) return;

    const normalizedLink = normalizeDeepLink(link);
    const finalLink = this.isMobile() && normalizedLink.startsWith('wc:')
      ? toMetaMaskWalletConnectUniversalLink(normalizedLink)
      : normalizedLink;

    // Dans Telegram, openLink() ouvre en externe SANS détruire le contexte JS de la mini-app.
    // window.location.href détruirait le contexte et tuerait le await de la requête WC.
    // Note : openLink() n'accepte QUE des URLs http(s) (il lève une exception sinon, qui fait
    // planter toute la mini-app) — finalLink doit donc toujours être une URL http(s) ici.
    if (this.isTelegramMiniApp()) {
      const tgWebApp = (window as any).Telegram?.WebApp;
      if (tgWebApp?.openLink) {
        tgWebApp.openLink(finalLink);
        return;
      }
    }

    if (this.isMobile()) {
      window.location.href = finalLink;
      return;
    }
    window.open(finalLink, '_blank');
  }

  private connectMobileDeepLink(wallet: 'metamask' | 'trust'): Promise<string> {
    const dappUrl = encodeURIComponent(window.location.href);
    const link = wallet === 'metamask'
      ? `https://metamask.app.link/dapp/${window.location.host}${window.location.pathname}`
      : `https://link.trustwallet.com/open_url?url=${dappUrl}`;

    this.openDeepLink(link);
    // The wallet app will reopen the DApp in its in-app browser where window.ethereum is injected.
    // When the user returns, they'll click again and hit the 'injected' path.
    return Promise.reject(new Error('deep_link_opened'));
  }

  /** WalletConnect v2 — works in Telegram, Android, any browser */
  async connectWalletConnect(): Promise<string> {
    try {
      this.patchWalletConnectLinksForAndroid();
      // Cast to Promise<any> so TypeScript never resolves WalletConnect's .d.ts files
      const { EthereumProvider } = await (import('@walletconnect/ethereum-provider') as Promise<any>);

      const showQrModal = true;
      this.wcProvider = await EthereumProvider.init({
        projectId: WC_PROJECT_ID,
        chains: [ETH_CHAIN.chainId],
        optionalChains: [BSC_CHAIN.chainId, BASE_CHAIN.chainId, ARBITRUM_CHAIN.chainId, ROBINHOOD_CHAIN.chainId],
        methods: WC_METHODS,
        optionalMethods: WC_METHODS,
        events: WC_EVENTS,
        optionalEvents: WC_EVENTS,
        showQrModal,
        rpcMap: {
          [ETH_CHAIN.chainId]: ETH_CHAIN.rpcUrl,
          [BSC_CHAIN.chainId]: BSC_CHAIN.rpcUrl,
          [BASE_CHAIN.chainId]: BASE_CHAIN.rpcUrl,
          [ARBITRUM_CHAIN.chainId]: ARBITRUM_CHAIN.rpcUrl,
          [ROBINHOOD_CHAIN.chainId]: ROBINHOOD_CHAIN.rpcUrl,
        },
        metadata: {
          ...this.getWalletConnectMetadata(),
        },
      });

      let timedOut = false;
      try {
        await Promise.race([
          this.wcProvider.connect(),
          new Promise((_, reject) => setTimeout(() => { timedOut = true; reject(new Error('wc_connect_timeout')); }, WC_CONNECT_TIMEOUT_MS)),
        ]);
      } catch (raceErr) {
        if (timedOut) {
          // Le relay n'a jamais répondu (approbation jamais reçue) — on nettoie le provider
          // resté à moitié initialisé plutôt que de le laisser traîner pour le prochain essai.
          const stale = this.wcProvider;
          this.wcProvider = null;
          stale?.disconnect?.().catch(() => {});
        }
        throw raceErr;
      }

      const accounts: string[] = this.wcProvider.accounts;
      if (!accounts.length) throw new Error('no_accounts');
      this.setupWcDisconnectHandler(this.wcProvider);
      this.wcLastSignAt = Date.now(); // connexion fraîche — relay actif
      this.patchWcProvider(this.wcProvider);
      if (this.isWcPeerMetaMask(this.wcProvider)) this.clearMetaMaskWcDeepLinkChoice();
      // Beaucoup de wallets mobiles (Trust Wallet et d'autres) renvoient l'adresse tout en
      // minuscule via WalletConnect, contrairement à MetaMask (checksummée) — normaliser ici
      // évite qu'une forme non-canonique ne se propage (Preferences, comparaisons ailleurs).
      return getAddress(accounts[0]);
    } catch (err: any) {
      if (err?.message?.includes('User rejected')) throw new Error('user_rejected');
      throw err;
    }
  }

  async disconnectWalletConnect() {
    if (this.wcProvider) {
      const provider = this.wcProvider;
      this.wcProvider = null; // état local réinitialisé immédiatement, sans attendre le relay
      // Fire-and-forget : provider.disconnect() attend un accusé de réception du relay WC,
      // qui peut ne jamais arriver si la session est justement morte/injoignable — même
      // défaut que tx.wait() côté transactions. On ne bloque plus l'app dessus.
      provider.disconnect().catch(() => {});
    }
    // Déconnexion volontaire — la signature mise en cache ne doit pas survivre au wallet qui l'a
    // produite (elle serait de toute façon ignorée au prochain appel, l'adresse ne matcherait
    // plus, mais autant ne pas laisser une signature valide traîner en stockage local une fois
    // le wallet explicitement déconnecté).
    this.walletAuthCache = null;
    await Preferences.remove({ key: Web3Service.WALLET_AUTH_PREF_KEY }).catch(() => {});
  }

  /**
   * Purge complète de la session WalletConnect : déconnexion du provider EN MÉMOIRE,
   * suppression de toutes les clés wc@ dans localStorage, et reset du timestamp.
   * À appeler avant de forcer une reconnexion après détection d'une session morte.
   */
  async forceWcClear(): Promise<void> {
    await this.disconnectWalletConnect();
    this.wcLastSignAt = 0;
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('wc@'))
        .forEach(k => localStorage.removeItem(k));
    } catch {}
  }

  /** Legacy helper kept for backward compat */
  async getConnectedAddress(): Promise<string | null> {
    try {
      return await this.connectInjected();
    } catch {
      return null;
    }
  }

  // ── Utils ──────────────────────────────────────────────────────────────────

  private async switchToEth(eth: any) {
    try {
      // Chain 1 (Ethereum Mainnet) is always present in wallets — switchEthereumChain is enough
      await eth.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: ETH_CHAIN.chainIdHex }],
      });
    } catch {
      // Ignore — wallet is likely already on Ethereum or switch was declined
    }
  }

  disconnect(): void {
    this.disconnectWalletConnect();
  }

  /** Retourne le provider actif (injected ou WalletConnect) */
  getProvider(): any {
    const injected = (window as any).ethereum;
    if (injected) return injected;
    if (this.wcProvider) this.patchWcProvider(this.wcProvider);
    return this.wcProvider ?? null;
  }

  async hasActiveWriteSession(expectedAddress?: string, chainId = ETH_CHAIN.chainId): Promise<boolean> {
    try {
      const eth = await this.ensureProvider();
      const isWc = eth === this.wcProvider;

      if (isWc) {
        if (!this.isWcSessionAlive()) return false;
        // NE PAS exiger que la chaîne CIBLE soit déjà approuvée dans la session : les wallets WC
        // (MetaMask/Trust) n'ajoutent pas à la session les chaînes qu'ils ne connaissent pas
        // nativement (RobinhoodChain 4663…), même listées en `optionalChains`. Exiger la chaîne
        // ici faisait échouer TOUT swap RobinhoodChain en WalletConnect (donc sur l'app Android
        // native) avec un « Session expirée » trompeur suivi d'un forceWcClear (re-scan QR
        // obligatoire), alors que la session est parfaitement valide — `getEvmSigner()` ajoute la
        // chaîne à la volée via `wallet_addEthereumChain` au moment de signer. On vérifie
        // seulement qu'une vraie session EVM existe (au moins un compte eip155).
        if (!this.wcSessionHasAnyEvmChain(eth)) return false;
        // Une session restaurée depuis le storage peut sembler valide (non expirée)
        // alors que le relay WebSocket sous-jacent n'est pas (encore) reconnecté —
        // les requêtes de signature seraient alors silencieusement perdues (le wallet
        // s'ouvre via notre notification, mais ne reçoit jamais la requête). On vérifie
        // que le relay répond réellement avant de faire confiance à la session.
        if (!(await this.probeWcRelayAlive(eth))) return false;
      }

      const accounts = isWc
        ? this.getProviderAccounts(eth)
        : await this.getInjectedAccounts(eth);
      if (!accounts.length) return false;

      if (expectedAddress) {
        return accounts.some((account) => account.toLowerCase() === expectedAddress.toLowerCase());
      }

      return true;
    } catch {
      return false;
    }
  }

  // Round-trip minime et non intrusif à travers le relay WC (aucune méthode de
  // signature, pas d'ouverture de wallet) — sert uniquement à confirmer que le
  // relay est effectivement connecté avant de faire confiance à une session restaurée.
  private async probeWcRelayAlive(eth: any, timeoutMs = 5_000): Promise<boolean> {
    try {
      await Promise.race([
        eth.request({ method: 'eth_chainId' }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('wc_relay_probe_timeout')), timeoutMs)),
      ]);
      return true;
    } catch {
      return false;
    }
  }

  async requireActiveWriteSession(expectedAddress?: string, chainId = ETH_CHAIN.chainId): Promise<void> {
    if (await this.hasActiveWriteSession(expectedAddress, chainId)) return;
    await this.forceWcClear();
    throw new Error('no_provider');
  }

  private getProviderAccounts(provider: any): string[] {
    const direct = provider?.accounts;
    if (Array.isArray(direct) && direct.length) return direct;

    const namespaces = provider?.session?.namespaces ?? {};
    return Object.values(namespaces)
      .flatMap((namespace: any) => Array.isArray(namespace?.accounts) ? namespace.accounts : [])
      .map((account: string) => account.split(':').pop() || '')
      .filter(Boolean);
  }

  private async getInjectedAccounts(provider: any): Promise<string[]> {
    try {
      const accounts = await provider.request({ method: 'eth_accounts' });
      return Array.isArray(accounts) ? accounts : [];
    } catch {
      return [];
    }
  }

  // True si la session WC en mémoire existe et n'est pas expirée
  private isWcSessionAlive(): boolean {
    const session = this.wcProvider?.session;
    return !!(session && session.expiry > Math.floor(Date.now() / 1000));
  }

  // La session WC contient-elle au moins une chaîne EVM approuvée (namespace eip155 non vide) ?
  // Sert de garde minimale « c'est bien une session EVM vivante » là où exiger une chaîne PRÉCISE
  // serait trop strict (voir hasActiveWriteSession + getEvmSigner : les chaînes exotiques comme
  // RobinhoodChain sont ajoutées au moment de signer, pas requises dans la session).
  private wcSessionHasAnyEvmChain(provider: any): boolean {
    const namespaces = provider?.session?.namespaces ?? {};
    return Object.values(namespaces).some((namespace: any) =>
      (Array.isArray(namespace?.accounts) && namespace.accounts.some((acc: string) => acc.startsWith('eip155:'))) ||
      (Array.isArray(namespace?.chains) && namespace.chains.some((chain: string) => chain.startsWith('eip155:'))));
  }

  // Écoute la déconnexion côté wallet (ex: user supprime la connexion dans MetaMask)
  // et invalide wcProvider pour forcer une nouvelle connexion au prochain appel.
  private setupWcDisconnectHandler(provider: any) {
    if (!provider?.on || provider.__wcDisconnectHandlerSet) return;
    provider.__wcDisconnectHandlerSet = true;
    provider.on('disconnect', () => { this.wcProvider = null; });
    provider.on('session_delete', () => { this.wcProvider = null; });
  }

  // Retourne le provider disponible. Vérifie l'expiry de la session en mémoire
  // avant de l'utiliser, et tente une restauration depuis localStorage si besoin.
  private async ensureProvider(): Promise<any> {
    const injected = (window as any).ethereum;
    if (injected) return injected;

    // Session en mémoire encore valide
    if (this.isWcSessionAlive()) {
      // Comme pour hasActiveWriteSession() : une session non expirée peut malgré tout
      // avoir un relay WebSocket mort (ex: WebView Telegram mis en arrière-plan pendant
      // l'aller-retour de connexion vers le wallet) — lui faire confiance sans vérifier
      // ferait échouer/boucler silencieusement la demande de signature qui suit (le wallet
      // s'ouvre via notre notification mais ne reçoit jamais la requête). On retente une
      // restauration propre de la session ci-dessous plutôt que de renvoyer ce provider.
      if (await this.probeWcRelayAlive(this.wcProvider)) {
        this.patchWcProvider(this.wcProvider);
        return this.wcProvider!;
      }
    }

    // Session expirée/absente en mémoire, ou relay injoignable — on tente une restauration
    // depuis le stockage (sans rien détruire tant qu'on n'est pas SÛR qu'il n'y a rien).
    this.wcProvider = null;
    if (await this.tryRestoreWcSession()) return this.wcProvider!;

    // Aucune session valide — purger les résidus WC du localStorage
    // pour forcer une reconnexion propre au prochain appel à /verification.
    await this.forceWcClear();
    throw new Error('no_provider');
  }

  // Config EthereumProvider des chemins de RESTAURATION (jamais de QR — showQrModal:false).
  // connectWalletConnect() garde la sienne (showQrModal:true + options de connexion).
  private async wcRestoreInit(): Promise<any> {
    const { EthereumProvider } = await (import('@walletconnect/ethereum-provider') as Promise<any>);
    return EthereumProvider.init({
      projectId: WC_PROJECT_ID,
      chains: [ETH_CHAIN.chainId],
      optionalChains: [BSC_CHAIN.chainId, BASE_CHAIN.chainId, ARBITRUM_CHAIN.chainId, ROBINHOOD_CHAIN.chainId],
      methods: WC_METHODS,
      optionalMethods: WC_METHODS,
      events: WC_EVENTS,
      optionalEvents: WC_EVENTS,
      showQrModal: false,
      rpcMap: {
        [ETH_CHAIN.chainId]: ETH_CHAIN.rpcUrl,
        [BSC_CHAIN.chainId]: BSC_CHAIN.rpcUrl,
        [BASE_CHAIN.chainId]: BASE_CHAIN.rpcUrl,
        [ARBITRUM_CHAIN.chainId]: ARBITRUM_CHAIN.rpcUrl,
        [ROBINHOOD_CHAIN.chainId]: ROBINHOOD_CHAIN.rpcUrl,
      },
      metadata: { ...this.getWalletConnectMetadata() },
    });
  }

  // Restaure une session WalletConnect depuis le stockage. Pose this.wcProvider + renvoie true
  // si une session non expirée existe ; NE DÉTRUIT RIEN en cas d'échec (contrairement à
  // l'ancien code inline d'ensureProvider). Partagé par ensureProvider() et warmWcSession().
  private async tryRestoreWcSession(): Promise<boolean> {
    try {
      const restored = await this.wcRestoreInit();
      const session = restored.session;
      if (session && session.expiry > Math.floor(Date.now() / 1000)) {
        this.wcProvider = restored;
        this.wcLastSignAt = 0; // session restaurée — relay froid, délai long au prochain sign
        this.setupWcDisconnectHandler(restored);
        this.patchWcProvider(restored);
        if (this.isWcPeerMetaMask(restored)) this.clearMetaMaskWcDeepLinkChoice();
        return true;
      }
    } catch { /* pas de session WC à restaurer */ }
    return false;
  }

  // Réchauffe une session WalletConnect existante AU DÉMARRAGE (voir app.component.ts), sans
  // jamais la détruire : restaure la session si elle est en stockage et réveille la socket relay
  // pour que la 1re signature ne parte pas à froid. Fire-and-forget — sans session restaurable,
  // no-op complet (contrairement à l'ancien forceWcClear() de démarrage qui, lui, effaçait
  // TOUTE session à chaque lancement → « Session expirée » systématique sur l'app Android).
  async warmWcSession(): Promise<void> {
    try {
      if ((window as any).ethereum) return;      // wallet injecté → pas de WalletConnect
      if (this.isWcSessionAlive()) return;       // déjà chargée en mémoire
      if (await this.tryRestoreWcSession()) {
        await this.wcProvider?.client?.core?.relayer?.transportOpen?.().catch(() => {});
      }
    } catch { /* démarrage : ne jamais bloquer sur ça */ }
  }

  // ── Ethereum Mainnet signer (for Earn/Staking transactions) ──────────────

  async getEthSigner(): Promise<any> {
    const eth = await this.ensureProvider();
    const isWc = eth === this.wcProvider;
    if (!isWc) await this.switchToEth(eth);
    if (isWc) {
      eth.__irisActiveChainId = ETH_CHAIN.chainId;
      this.patchWcProvider(eth);
    }
    // Mémorisé pour signFreshAuth() — voir son commentaire sur SIGN_MESSAGE_TIMEOUT_MS, qui ne
    // doit s'appliquer QUE quand ce signer vient de WalletConnect.
    this.lastEthSignerWasWc = isWc;
    const provider = new BrowserProvider(eth);
    return provider.getSigner();
  }

  private walletAuthCache: WalletAuthCacheEntry | null = null;
  private lastEthSignerWasWc = false;

  // Durée du cache — étendue de 10 à 55 min et PERSISTÉE (Capacitor Preferences), pour survivre à
  // une relance de l'app (le cache mémoire seul était remis à zéro à chaque redémarrage, fréquent
  // en mini-app Telegram/mobile, ce qui redemandait une signature bien plus souvent qu'il n'y
  // paraissait nécessaire). Reste sous la fenêtre anti-rejeu serveur de 60 min (voir
  // WalletAuthService.MaxClockSkew côté IrisApi). Compromis assumé : une signature exfiltrée
  // depuis le stockage local de l'appareil resterait rejouable jusqu'à 60 min (contre quelques
  // minutes avant, le cache mémoire étant plus difficile à extraire qu'un Preferences en clair).
  private static readonly WALLET_AUTH_TTL_MS  = 55 * 60 * 1000;
  private static readonly WALLET_AUTH_PREF_KEY = 'walletAuthCache';

  // Prouve à IrisApi que l'appelant contrôle bien ce wallet (signature EIP-191, personal_sign) —
  // requis par les endpoints qui modifient une ressource liée à un wallet (wallet/credit-referrer,
  // stakeholders/disable) : la clé API partagée seule ne prouve que "c'est l'app", pas "c'est le
  // propriétaire de CE wallet précis". Le timestamp fait office de protection anti-rejeu côté
  // serveur. Une vérification on-chain (solde, staking…) ne peut PAS remplacer cette preuve : ces
  // données sont publiques, lisibles pour n'importe quelle adresse sans la posséder — seule une
  // signature prouve la possession de la clé privée.
  async signWalletAuthHeaders(): Promise<WalletAuthHeaders> {
    const signer  = await this.getEthSigner();
    const address = await signer.getAddress();

    if (this.walletAuthCache && this.isWalletAuthCacheValid(this.walletAuthCache, address)) {
      return this.walletAuthCache.headers;
    }

    const persisted = await this.loadPersistedWalletAuth(address);
    if (persisted) {
      this.walletAuthCache = persisted;
      return persisted.headers;
    }

    return this.signFreshAuth(signer, address);
  }

  // Variante pour les appels DIFFÉRÉS (ex: recordReward() après un swap, où plusieurs secondes —
  // le temps qu'une transaction confirme — séparent l'action de l'utilisateur de cet appel). Dans
  // cet intervalle, le compte ACTIF dans l'extension a pu changer (plusieurs comptes MetaMask,
  // l'utilisateur en consulte un autre entre-temps) — signWalletAuthHeaders() signerait alors pour
  // CE nouveau compte actif, pas pour `targetAddress` (celui qui a réellement fait l'action), et le
  // backend rejetterait silencieusement (WalletAddress ≠ signataire), voir recordReward().
  //
  // Ici on ne signe QUE si le compte actif correspond déjà à targetAddress — sinon on retourne
  // null plutôt que de déclencher une signature pour le mauvais wallet (qui échouerait de toute
  // façon côté serveur, en pire silencieusement).
  async getWalletAuthHeadersFor(targetAddress: string): Promise<WalletAuthHeaders | null> {
    if (this.walletAuthCache && this.isWalletAuthCacheValid(this.walletAuthCache, targetAddress)) {
      return this.walletAuthCache.headers;
    }

    const persisted = await this.loadPersistedWalletAuth(targetAddress);
    if (persisted) {
      this.walletAuthCache = persisted;
      return persisted.headers;
    }

    const signer  = await this.getEthSigner();
    const address = await signer.getAddress();
    if (address.toLowerCase() !== targetAddress.toLowerCase()) return null;

    return this.signFreshAuth(signer, address);
  }

  // Variante pour les filets de sécurité en arrière-plan (ex: retryReferralCreditIfNeeded() dans
  // earn.page.ts, qui retente un crédit parrain resté bloqué après une signature ratée/ignorée
  // juste après un stake) : ne JAMAIS déclencher de popup de signature surprise sans action
  // explicite de l'utilisateur — retourne null s'il n'y a pas déjà une signature valide en cache
  // (mémoire ou persistée), sans jamais appeler getEthSigner()/signFreshAuth().
  async getCachedWalletAuthHeaders(address: string): Promise<WalletAuthHeaders | null> {
    if (this.walletAuthCache && this.isWalletAuthCacheValid(this.walletAuthCache, address)) {
      return this.walletAuthCache.headers;
    }

    const persisted = await this.loadPersistedWalletAuth(address);
    if (persisted) {
      this.walletAuthCache = persisted;
      return persisted.headers;
    }

    return null;
  }

  private async signFreshAuth(signer: any, address: string): Promise<WalletAuthHeaders> {
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const message = `Iris Auth Request\nTimestamp: ${timestamp}`;
    // BUG CORRIGÉ (2026-09-15, signalé par l'utilisateur — accès agent-subscribe cassé sur
    // MetaMask/Trust Wallet, "Impossible de vérifier ton abonnement" à chaque fois) : le timeout
    // ci-dessous a été introduit (voir SIGN_MESSAGE_TIMEOUT_MS) UNIQUEMENT pour l'aller-retour OS
    // fragile de WalletConnect sur Telegram mobile — mais appliqué ici à TOUTE signature, y
    // compris le navigateur intégré d'un wallet (MetaMask/Trust Wallet, window.ethereum direct,
    // AUCUN aller-retour WalletConnect en jeu, voir requiresExplicitReconnect() juste au-dessus).
    // Un utilisateur qui prend simplement plus de 45s pour lire/approuver dans son wallet se
    // retrouvait avec un rejet définitif au lieu d'attendre sa réponse, comme avant ce fix. Le
    // timeout ne s'applique donc plus qu'au cas qu'il visait réellement.
    const signature = this.lastEthSignerWasWc
      ? await Promise.race([
          signer.signMessage(message),
          new Promise<string>((_, reject) =>
            setTimeout(() => reject(new Error('sign_message_timeout')), SIGN_MESSAGE_TIMEOUT_MS)),
        ])
      : await signer.signMessage(message);
    const headers = { 'X-Wallet-Address': address, 'X-Wallet-Signature': signature, 'X-Wallet-Timestamp': timestamp };
    const entry: WalletAuthCacheEntry = { headers, expiresAt: Date.now() + Web3Service.WALLET_AUTH_TTL_MS };

    this.walletAuthCache = entry;
    await Preferences.set({ key: Web3Service.WALLET_AUTH_PREF_KEY, value: JSON.stringify(entry) }).catch(() => {});
    return headers;
  }

  private isWalletAuthCacheValid(entry: WalletAuthCacheEntry, address: string): boolean {
    return Date.now() < entry.expiresAt
      && entry.headers['X-Wallet-Address'].toLowerCase() === address.toLowerCase();
  }

  // null si absent, expiré, ou signé pour une AUTRE adresse (ex: wallet changé entre-temps) —
  // dans tous ces cas on retombe silencieusement sur une nouvelle signature.
  private async loadPersistedWalletAuth(address: string): Promise<WalletAuthCacheEntry | null> {
    try {
      const stored = await Preferences.get({ key: Web3Service.WALLET_AUTH_PREF_KEY });
      if (!stored.value) return null;
      const entry: WalletAuthCacheEntry = JSON.parse(stored.value);
      return this.isWalletAuthCacheValid(entry, address) ? entry : null;
    } catch {
      return null;
    }
  }

  // Intercepte request() du provider WC pour:
  // 1) ouvrir automatiquement le wallet lors d'une demande de signature mobile;
  // 2) fallback les lectures RPC que certains wallets WC ne servent pas.
  private patchWcProvider(wcEth: any) {
    if (wcEth.__irisWcPatchApplied) return;
    wcEth.__irisWcPatchApplied = true;

    const SIGN_METHODS = new Set([
      'eth_sendTransaction', 'eth_sign', 'personal_sign',
      'eth_signTypedData', 'eth_signTypedData_v4',
    ]);
    const original = wcEth.request.bind(wcEth);
    wcEth.request = async (args: any) => {
      const method = args?.method;
      try {
        if (this.isMobile() && SIGN_METHODS.has(method)) {
          this.clearMetaMaskWcDeepLinkChoice();
        }
        const promise = original(args);
        if (this.isMobile() && SIGN_METHODS.has(method)) {
          // Délai adaptatif selon le temps écoulé depuis la dernière signature :
          //  < 2 min  → relay encore chaud → délai court (WC_MOBILE_SIGN_OPEN_DELAY_MS)
          //  ≥ 2 min  → relay potentiellement froid (reconnexion WebSocket requise) → 4500ms
          // Le délai long couvre le cas extrême : session restaurée après des jours/semaines
          // (DNS + TLS + WS handshake + relay auth + livraison à MetaMask ≈ 1-4 sec).
          const isMetaMask = this.isWcPeerMetaMask(wcEth);
          const secondsSinceLastSign = (Date.now() - this.wcLastSignAt) / 1000;
          const warmDelay = isMetaMask ? WC_METAMASK_SIGN_OPEN_DELAY_MS : WC_MOBILE_SIGN_OPEN_DELAY_MS;
          const coldDelay = isMetaMask ? 8_000 : 4_500;
          const delay = secondsSinceLastSign < 120 ? warmDelay : coldDelay;
          let settled = false;
          promise.finally(() => { settled = true; }).catch(() => {});
          // Gardé par `settled` : si la signature a déjà abouti avant ce délai, rouvrir le
          // wallet est inutile et se voit comme une (fausse) nouvelle demande de signature.
          setTimeout(() => {
            if (!settled) this.openWalletForSigning(wcEth);
          }, delay);
          if (isMetaMask) {
            setTimeout(() => {
              if (!settled) this.openWalletForSigning(wcEth);
            }, delay + 8_000);
          }
        }
        const result = SIGN_METHODS.has(method)
          ? await Promise.race([
              promise,
              new Promise((_, reject) => setTimeout(() => reject(new Error('wc_sign_timeout')), WC_SIGN_TIMEOUT_MS)),
            ])
          : await promise;
        if (SIGN_METHODS.has(method)) this.wcLastSignAt = Date.now();
        if (method === 'wallet_switchEthereumChain') this.setWcActiveChain(wcEth, args?.params?.[0]?.chainId);
        return result;
      } catch (err) {
        if (WC_RPC_FALLBACK_METHODS.has(method) && this.isUnsupportedWalletRpcError(err)) {
          return this.callFallbackRpc(wcEth, method, args?.params ?? []);
        }
        throw err;
      }
    };
  }

  private setWcActiveChain(wcEth: any, chainId: any) {
    if (typeof chainId === 'string' && chainId.startsWith('0x')) {
      wcEth.__irisActiveChainId = parseInt(chainId, 16);
    } else if (chainId) {
      wcEth.__irisActiveChainId = Number(chainId);
    }
  }

  private getWcActiveChainId(wcEth: any): number {
    const explicit = Number(wcEth?.__irisActiveChainId || wcEth?.chainId);
    if (explicit) return explicit;

    const namespaces = wcEth?.session?.namespaces ?? {};

    // Format explicite : namespace.chains = ["eip155:56", ...]
    const firstChain = Object.values(namespaces)
      .flatMap((namespace: any) => Array.isArray(namespace?.chains) ? namespace.chains : [])
      .find((chain: string) => chain.startsWith('eip155:'));
    if (firstChain) return Number(String(firstChain).split(':')[1]);

    // Format Trust Wallet : namespace.accounts = ["eip155:56:0x...", ...]
    // Trust Wallet met les chaînes dans accounts, pas dans chains
    const firstAccount = Object.values(namespaces)
      .flatMap((namespace: any) => Array.isArray(namespace?.accounts) ? namespace.accounts : [])
      .find((acc: string) => acc.startsWith('eip155:'));
    if (firstAccount) return Number(String(firstAccount).split(':')[1]);

    return ETH_CHAIN.chainId;
  }

  private isUnsupportedWalletRpcError(err: any): boolean {
    // JSON.stringify(new Error(...)) = '{}' — il faut aussi vérifier .message et .reason
    const msg = (
      JSON.stringify(err ?? '') + ' ' +
      (err?.message ?? '') + ' ' +
      (err?.reason ?? '') + ' ' +
      (err?.error?.message ?? '')
    ).toLowerCase();
    return msg.includes('missing or invalid')
      || msg.includes('method not found')
      || msg.includes('unsupported method')
      || msg.includes('unknown method')
      || msg.includes('does not exist')
      || msg.includes('not supported')
      || err?.code === -32601
      || err?.code === 5100; // Trust Wallet : méthode non autorisée via WC relay
  }

  private async callFallbackRpc(wcEth: any, method: string, params: any[]): Promise<any> {
    const chainId = this.getWcActiveChainId(wcEth);
    const rpcUrls = chainId === BSC_CHAIN.chainId ? BSC_RPC_FALLBACK_URLS
      : chainId === BASE_CHAIN.chainId ? BASE_RPC_FALLBACK_URLS
      : chainId === ARBITRUM_CHAIN.chainId ? ARBITRUM_RPC_FALLBACK_URLS
      : chainId === ROBINHOOD_CHAIN.chainId ? ROBINHOOD_RPC_FALLBACK_URLS
      : ETH_RPC_FALLBACK_URLS;
    let lastError: any = null;

    for (const rpcUrl of rpcUrls) {
      try {
        const response = await fetch(rpcUrl, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', id: Date.now(), method, params }),
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const json = await response.json();
        if (json?.error) throw new Error(json.error.message || `RPC ${method} failed`);
        if (Object.prototype.hasOwnProperty.call(json ?? {}, 'result')) return json.result;
        throw new Error(`RPC ${method} returned no result`);
      } catch (err: any) {
        lastError = err;
      }
    }

    throw new Error(`RPC fallback ${method} failed on chain ${chainId}: ${lastError?.message || lastError || 'unknown error'}`);
  }

  private isWcPeerMetaMask(wcEth: any): boolean {
    const meta = wcEth?.session?.peer?.metadata;
    const text = `${meta?.name ?? ''} ${meta?.url ?? ''} ${meta?.redirect?.native ?? ''} ${meta?.redirect?.universal ?? ''}`.toLowerCase();
    return text.includes('metamask');
  }

  private getStoredWcDeepLinkChoice(): any {
    try {
      const raw = localStorage.getItem(WC_DEEPLINK_CHOICE_KEY);
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch {
        return raw;
      }
    } catch {
      return null;
    }
  }

  private isMetaMaskDeepLinkChoice(choice: any): boolean {
    const text = typeof choice === 'string'
      ? choice.toLowerCase()
      : `${choice?.name ?? ''} ${choice?.href ?? ''}`.toLowerCase();
    return text.includes('metamask') || text.includes('metamask://') || text.includes('metamask.app.link');
  }

  private clearMetaMaskWcDeepLinkChoice(): void {
    try {
      if (this.isMetaMaskDeepLinkChoice(this.getStoredWcDeepLinkChoice())) {
        localStorage.removeItem(WC_DEEPLINK_CHOICE_KEY);
      }
    } catch {}
  }

  suppressWalletOpen(durationMs = 10_000) {
    this._suppressWalletOpenUntil = Date.now() + durationMs;
  }

  // Ré-ouverture du wallet DÉCLENCHÉE PAR L'UTILISATEUR (bouton "Ouvrir mon wallet" pendant
  // qu'une signature est en attente). L'ouverture auto de patchWcProvider() se fait après un
  // délai adaptatif et peut échouer silencieusement (relay froid, retour au 1er plan raté en
  // Mini App Telegram) : l'utilisateur reste alors devant un spinner muet sans savoir qu'une
  // requête l'attend dans son wallet. Ce point d'entrée manuel lève la suppression en cours et
  // force le deep link vers le wallet connecté. No-op hors WalletConnect (wallet injecté = déjà
  // au 1er plan) ou sur desktop.
  reopenWalletForSigning(): boolean {
    if (!this.wcProvider || !this.isMobile()) return false;
    this._suppressWalletOpenUntil = 0;
    this.openWalletForSigning(this.wcProvider);
    return true;
  }

  private openWalletForSigning(wcEth: any) {
    if (Date.now() < this._suppressWalletOpenUntil) return;
    const meta      = wcEth?.session?.peer?.metadata;
    const redirect  = meta?.redirect;
    const native    = redirect?.native;
    const universal = redirect?.universal;
    const isMetaMask = this.isWcPeerMetaMask(wcEth);

    if (isMetaMask) {
      // MetaMask iOS ouvre parfois l'app sans afficher la requête quand on force
      // metamask://wc?requestId=...&sessionTopic=... depuis Telegram. Le lien
      // universel laisse MetaMask récupérer la requête pending via son relay WC.
      this.openDeepLink(universal || 'https://metamask.app.link');
      return;
    }

    if (universal || native) {
      // Le lien universel route plus régulièrement vers la requête WC en attente.
      this.openDeepLink(universal || native!);
      return;
    }

    // Fallback basé sur le nom du wallet dans les métadonnées WC.
    // Certaines versions de Trust Wallet ne fournissent pas de redirect.
    const name = (meta?.name ?? '').toLowerCase();
    const fallback = name.includes('trust')
      ? `https://link.trustwallet.com/open_url?url=${APP_URL}`
      : 'https://metamask.app.link';
    this.openDeepLink(fallback);
  }

  // ── Signer EVM générique (BSC, Base, Arbitrum, Robinhood Chain…) ──────────
  // Demande au wallet connecté de basculer sur la chaîne visée (wallet_switchEthereumChain),
  // ou de l'ajouter s'il ne la connaît pas encore (wallet_addEthereumChain).

  async getEvmSigner(chain: { chainId: number; chainIdHex: string; name: string; currency: string; rpcUrl: string; explorerUrl: string }): Promise<any> {
    const addChainParams = {
      chainId: chain.chainIdHex,
      chainName: chain.name,
      nativeCurrency: { name: chain.currency, symbol: chain.currency, decimals: 18 },
      rpcUrls: [chain.rpcUrl],
      blockExplorerUrls: [chain.explorerUrl],
    };
    const eth = await this.ensureProvider();
    const isWc = eth === this.wcProvider;
    if (isWc) this.patchWcProvider(eth);

    // BUG CORRIGÉ (2026-09-08, app Android native uniquement) : avant, si la session WalletConnect
    // ne listait pas la chaîne cible dans son namespace, on faisait `disconnectWalletConnect()` +
    // `throw 'no_provider'` → l'UI affichait « Session expirée — reconnectez votre wallet » et
    // l'utilisateur devait re-scanner le QR. C'était SYSTÉMATIQUE pour RobinhoodChain (4663) :
    // MetaMask/Trust n'ajoutent jamais à la session une chaîne qu'ils ne connaissent pas
    // nativement, même listée en `optionalChains`. Donc TOUT swap RobinhoodChain échouait sur
    // l'app Android (le web/desktop passe par un wallet injecté, qui ne fait jamais ce contrôle).
    // Or ajouter une chaîne inconnue au wallet, c'est précisément le rôle de
    // `wallet_addEthereumChain` juste en dessous. On tente donc directement le switch/add, et on
    // ne touche JAMAIS à la session : une chaîne absente de la session n'est pas une session morte.
    try {
      await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: chain.chainIdHex }] });
    } catch (err: any) {
      // 4001 = l'utilisateur a refusé dans le wallet → on laisse remonter tel quel.
      // Tout le reste (4902 / -32603 « chaîne inconnue », ou le rejet interne du provider WC
      // quand la chaîne n'est pas dans la session) → on demande au wallet de l'AJOUTER. Si cet
      // ajout échoue à son tour, l'erreur remonte telle quelle (message actionnable, jamais
      // 'no_provider' qui déclencherait à tort le flux « session expirée »).
      if (err?.code === 4001) throw err;
      await eth.request({ method: 'wallet_addEthereumChain', params: [addChainParams] });
    }
    if (isWc) eth.__irisActiveChainId = chain.chainId;

    const provider = new BrowserProvider(eth);
    return provider.getSigner();
  }

  // ── BSC signer (for P2P transactions) ─────────────────────────────────────

  async getBscSigner(): Promise<any> {
    return this.getEvmSigner(BSC_CHAIN);
  }
}
